import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { 
  ArrowTrendingDownIcon,
  BanknotesIcon,
  ClockIcon,
  CalendarDaysIcon,
  BuildingStorefrontIcon,
  WrenchScrewdriverIcon,
  ShoppingCartIcon,
  LightBulbIcon,
  PaperAirplaneIcon,
  PlusIcon,
  MagnifyingGlassIcon,
  ChartBarIcon,
  FolderIcon,
  XMarkIcon
} from '@heroicons/react/24/outline';

const ExpenseTracker = () => {
  const [period, setPeriod] = useState('month');
  const [month, setMonth] = useState('07'); // Juillet
  const [year, setYear] = useState('2023');
  const [categoryFilter, setCategoryFilter] = useState('all');
  const [showAddExpenseModal, setShowAddExpenseModal] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');

  // Données fictives des catégories de dépenses
  const expenseCategories = [
    { id: 'rent', name: 'Loyer', icon: BuildingStorefrontIcon, color: 'blue' },
    { id: 'utilities', name: 'Charges', icon: LightBulbIcon, color: 'yellow' },
    { id: 'maintenance', name: 'Maintenance', icon: WrenchScrewdriverIcon, color: 'orange' },
    { id: 'equipment', name: 'Équipement', icon: ShoppingCartIcon, color: 'green' },
    { id: 'salaries', name: 'Salaires', icon: BanknotesIcon, color: 'purple' },
    { id: 'marketing', name: 'Marketing', icon: PaperAirplaneIcon, color: 'red' },
    { id: 'other', name: 'Autres', icon: FolderIcon, color: 'gray' }
  ];

  // Données fictives des dépenses par catégorie
  const expensesByCategory = [
    { 
      category: 'rent',
      amount: 850000, 
      percentage: 30,
      trend: '+0%',
      description: 'Location des locaux'
    },
    { 
      category: 'salaries', 
      amount: 960000, 
      percentage: 34,
      trend: '+5%',
      description: 'Salaires et primes des employés'
    },
    { 
      category: 'utilities', 
      amount: 285000, 
      percentage: 10, 
      trend: '+3%',
      description: 'Électricité, eau, internet'
    },
    { 
      category: 'maintenance', 
      amount: 170000, 
      percentage: 6, 
      trend: '-12%',
      description: 'Réparation et entretien'
    },
    { 
      category: 'equipment', 
      amount: 340000, 
      percentage: 12, 
      trend: '+40%',
      description: 'Achat de nouveaux équipements'
    },
    { 
      category: 'marketing', 
      amount: 170000, 
      percentage: 6, 
      trend: '+8%',
      description: 'Publicité et promotion'
    },
    { 
      category: 'other', 
      amount: 75000, 
      percentage: 2, 
      trend: '-5%',
      description: 'Dépenses diverses'
    }
  ];

  // Données fictives des transactions récentes
  const recentExpenses = [
    {
      id: 'exp1',
      date: '2023-07-18',
      category: 'equipment',
      amount: 120000,
      supplier: 'FitnessEquip Pro',
      description: 'Achat de 2 nouveaux tapis roulants',
      paymentMethod: 'virement',
      status: 'completed'
    },
    {
      id: 'exp2',
      date: '2023-07-15',
      category: 'salaries',
      amount: 960000,
      supplier: 'Employés',
      description: 'Salaires Juillet 2023',
      paymentMethod: 'virement',
      status: 'completed'
    },
    {
      id: 'exp3',
      date: '2023-07-10',
      category: 'rent',
      amount: 850000,
      supplier: 'Agence Immobilière Alger',
      description: 'Loyer Juillet 2023',
      paymentMethod: 'virement',
      status: 'completed'
    },
    {
      id: 'exp4',
      date: '2023-07-08',
      category: 'marketing',
      amount: 60000,
      supplier: 'Social Media Pro',
      description: 'Campagne publicitaire Facebook & Instagram',
      paymentMethod: 'carte',
      status: 'completed'
    },
    {
      id: 'exp5',
      date: '2023-07-05',
      category: 'utilities',
      amount: 95000,
      supplier: 'Sonelgaz',
      description: 'Facture électricité Juin 2023',
      paymentMethod: 'prélèvement',
      status: 'completed'
    },
    {
      id: 'exp6',
      date: '2023-07-02',
      category: 'maintenance',
      amount: 75000,
      supplier: 'TechFix Services',
      description: 'Réparation climatisation',
      paymentMethod: 'espèces',
      status: 'completed'
    },
    {
      id: 'exp7',
      date: '2023-06-28',
      category: 'equipment',
      amount: 220000,
      supplier: 'FitnessEquip Pro',
      description: 'Achat banc musculation et poids',
      paymentMethod: 'virement',
      status: 'completed'
    }
  ];

  // Données historiques des dépenses mensuelles
  const monthlyExpenseHistory = [
    { month: 'Janvier', amount: 2650000, trend: '' },
    { month: 'Février', amount: 2680000, trend: '+1.1%' },
    { month: 'Mars', amount: 2700000, trend: '+0.7%' },
    { month: 'Avril', amount: 2750000, trend: '+1.8%' },
    { month: 'Mai', amount: 2800000, trend: '+1.8%' },
    { month: 'Juin', amount: 2825000, trend: '+0.9%' },
    { month: 'Juillet', amount: 2850000, trend: '+0.9%' }
  ];

  // Calculs des totaux
  const totalExpenses = expensesByCategory.reduce((sum, category) => sum + category.amount, 0);

  // Filtrer les dépenses récentes
  const filteredExpenses = recentExpenses.filter(expense => {
    const matchesSearch = searchTerm === '' || 
      expense.description.toLowerCase().includes(searchTerm.toLowerCase()) ||
      expense.supplier.toLowerCase().includes(searchTerm.toLowerCase());
    
    const matchesCategory = categoryFilter === 'all' || expense.category === categoryFilter;
    
    return matchesSearch && matchesCategory;
  });

  // Formatage des nombres pour l'affichage
  const formatCurrency = (value) => value.toLocaleString('fr-DZ');
  
  // Formatage des dates
  const formatDate = (dateString) => {
    const options = { day: '2-digit', month: '2-digit', year: 'numeric' };
    return new Date(dateString).toLocaleDateString('fr-FR', options);
  };

  // Récupérer l'icône pour une catégorie
  const getCategoryIcon = (categoryId) => {
    const category = expenseCategories.find(cat => cat.id === categoryId);
    const IconComponent = category ? category.icon : FolderIcon;
    return <IconComponent className={`h-5 w-5 text-${category?.color || 'gray'}-400`} />;
  };

  // Récupérer le nom pour une catégorie
  const getCategoryName = (categoryId) => {
    const category = expenseCategories.find(cat => cat.id === categoryId);
    return category ? category.name : 'Autre';
  };

  // Conversion des périodes
  const periodLabels = {
    'day': 'Aujourd\'hui',
    'week': 'Cette semaine',
    'month': 'Ce mois',
    'quarter': 'Ce trimestre',
    'year': 'Cette année'
  };

  return (
    <div className="space-y-6">
      {/* En-tête avec sélection de période */}
      <div className="bg-white/5 backdrop-blur-sm rounded-xl p-6 border border-white/10">
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
          <div className="flex items-center">
            <BanknotesIcon className="h-6 w-6 text-red-400 mr-2" />
            <h2 className="text-xl font-bold text-white">Suivi des dépenses</h2>
          </div>
          
          <div className="flex flex-wrap gap-2">
            <select
              value={month}
              onChange={(e) => setMonth(e.target.value)}
              className="bg-white/10 border border-white/20 rounded-lg px-3 py-2 text-white"
            >
              <option value="01" className="bg-slate-800">Janvier</option>
              <option value="02" className="bg-slate-800">Février</option>
              <option value="03" className="bg-slate-800">Mars</option>
              <option value="04" className="bg-slate-800">Avril</option>
              <option value="05" className="bg-slate-800">Mai</option>
              <option value="06" className="bg-slate-800">Juin</option>
              <option value="07" className="bg-slate-800">Juillet</option>
            </select>
            
            <select
              value={year}
              onChange={(e) => setYear(e.target.value)}
              className="bg-white/10 border border-white/20 rounded-lg px-3 py-2 text-white"
            >
              <option value="2023" className="bg-slate-800">2023</option>
              <option value="2022" className="bg-slate-800">2022</option>
            </select>
            
            <button 
              onClick={() => setShowAddExpenseModal(true)}
              className="bg-red-600 hover:bg-red-700 text-white px-3 py-2 rounded-lg flex items-center gap-1 transition-colors"
            >
              <PlusIcon className="h-5 w-5" />
              <span>Ajouter dépense</span>
            </button>
          </div>
        </div>
      </div>
      
      {/* Cartes de statistiques principales */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.3, delay: 0.1 }}
          className="bg-gradient-to-br from-red-500/20 to-red-600/20 border border-red-500/20 backdrop-blur-sm rounded-xl p-6"
        >
          <div className="flex items-start justify-between">
            <div>
              <p className="text-red-300 text-sm">Total Dépenses</p>
              <h3 className="text-2xl font-bold text-white mt-1">{formatCurrency(totalExpenses)} DA</h3>
            </div>
            <div className="p-2 bg-red-500/20 rounded-lg">
              <BanknotesIcon className="h-6 w-6 text-red-300" />
            </div>
          </div>
          <div className="flex items-center mt-4">
            <ArrowTrendingDownIcon className="h-4 w-4 text-red-400" />
            <span className="text-red-400 text-sm ml-1">+0.9%</span>
            <span className="text-white/50 text-xs ml-2">vs mois précédent</span>
          </div>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.3, delay: 0.2 }}
          className="bg-gradient-to-br from-purple-500/20 to-purple-600/20 border border-purple-500/20 backdrop-blur-sm rounded-xl p-6"
        >
          <div className="flex items-start justify-between">
            <div>
              <p className="text-purple-300 text-sm">Dépense Principale</p>
              <h3 className="text-2xl font-bold text-white mt-1">Salaires</h3>
            </div>
            <div className="p-2 bg-purple-500/20 rounded-lg">
              <BanknotesIcon className="h-6 w-6 text-purple-300" />
            </div>
          </div>
          <div className="flex items-center mt-4">
            <span className="text-white/60 text-sm">{formatCurrency(960000)} DA (34% du total)</span>
          </div>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.3, delay: 0.3 }}
          className="bg-gradient-to-br from-orange-500/20 to-orange-600/20 border border-orange-500/20 backdrop-blur-sm rounded-xl p-6"
        >
          <div className="flex items-start justify-between">
            <div>
              <p className="text-orange-300 text-sm">Prévisions Août</p>
              <h3 className="text-2xl font-bold text-white mt-1">{formatCurrency(2900000)} DA</h3>
            </div>
            <div className="p-2 bg-orange-500/20 rounded-lg">
              <CalendarDaysIcon className="h-6 w-6 text-orange-300" />
            </div>
          </div>
          <div className="flex items-center mt-4">
            <ArrowTrendingDownIcon className="h-4 w-4 text-orange-400" />
            <span className="text-orange-400 text-sm ml-1">+1.8%</span>
            <span className="text-white/50 text-xs ml-2">estimé</span>
          </div>
        </motion.div>
      </div>

      {/* Répartition des dépenses par catégorie */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.3, delay: 0.4 }}
        className="bg-white/5 backdrop-blur-sm rounded-xl p-6 border border-white/10"
      >
        <div className="flex items-center mb-4">
          <ChartBarIcon className="h-6 w-6 text-blue-400 mr-2" />
          <h2 className="text-xl font-bold text-white">Répartition des dépenses</h2>
        </div>

        <div className="space-y-4">
          {expensesByCategory.map((item, index) => (
            <div key={index} className="bg-white/5 rounded-lg p-4">
              <div className="flex items-center justify-between mb-2">
                <div className="flex items-center">
                  {getCategoryIcon(item.category)}
                  <div className="ml-3">
                    <h3 className="text-white font-medium">{getCategoryName(item.category)}</h3>
                    <p className="text-white/60 text-sm">
                      {item.description}
                    </p>
                  </div>
                </div>
                <div className="text-right">
                  <p className="text-white font-semibold">{formatCurrency(item.amount)} DA</p>
                  <p className={`text-sm ${!item.trend.includes('-') ? 'text-red-400' : 'text-green-400'}`}>
                    {item.trend} vs mois précédent
                  </p>
                </div>
              </div>
              <div className="w-full bg-white/10 h-2 rounded-full mt-2">
                <div 
                  className={`h-2 rounded-full bg-${expenseCategories.find(cat => cat.id === item.category)?.color || 'gray'}-500/60`}
                  style={{ width: `${item.percentage}%` }}
                ></div>
              </div>
            </div>
          ))}
        </div>

        <div className="mt-6 p-4 bg-white/5 rounded-lg">
          <div className="text-center">
            <h3 className="text-lg font-semibold text-white">Total Dépenses: {formatCurrency(totalExpenses)} DA</h3>
            <p className="text-white/60 mt-1">Période: {month}/{year}</p>
          </div>
        </div>
      </motion.div>
      
      {/* Historique des dépenses mensuelles */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.3, delay: 0.5 }}
        className="bg-white/5 backdrop-blur-sm rounded-xl p-6 border border-white/10"
      >
        <div className="flex items-center mb-4">
          <CalendarDaysIcon className="h-6 w-6 text-blue-400 mr-2" />
          <h2 className="text-xl font-bold text-white">Historique des dépenses</h2>
        </div>

        <div className="overflow-x-auto">
          <div className="flex flex-nowrap gap-4 pb-4">
            {monthlyExpenseHistory.map((item, index) => (
              <div 
                key={index} 
                className={`flex-none w-44 p-4 rounded-lg ${month === String(index + 1).padStart(2, '0') ? 'bg-blue-500/20 border border-blue-500/30' : 'bg-white/5'}`}
              >
                <h3 className="text-white font-medium">{item.month} 2023</h3>
                <p className="text-white text-lg font-semibold mt-2">{formatCurrency(item.amount)} DA</p>
                {item.trend && (
                  <div className="flex items-center mt-1">
                    <span className="text-white/50 text-sm">
                      {item.trend} 
                    </span>
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      </motion.div>

      {/* Liste des dépenses récentes */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.3, delay: 0.6 }}
        className="bg-white/5 backdrop-blur-sm rounded-xl p-6 border border-white/10"
      >
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4 mb-6">
          <div className="flex items-center">
            <ClockIcon className="h-6 w-6 text-blue-400 mr-2" />
            <h2 className="text-xl font-bold text-white">Dépenses récentes</h2>
          </div>
          
          <div className="flex flex-wrap gap-2">
            <div className="relative flex-1">
              <MagnifyingGlassIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-white/40" />
              <input
                type="text"
                placeholder="Rechercher..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-10 pr-4 py-2 bg-white/10 border border-white/20 rounded-lg text-white placeholder-white/50"
              />
            </div>
            
            <select
              value={categoryFilter}
              onChange={(e) => setCategoryFilter(e.target.value)}
              className="bg-white/10 border border-white/20 rounded-lg px-3 py-2 text-white"
            >
              <option value="all" className="bg-slate-800">Toutes les catégories</option>
              {expenseCategories.map(cat => (
                <option key={cat.id} value={cat.id} className="bg-slate-800">{cat.name}</option>
              ))}
            </select>
          </div>
        </div>

        {filteredExpenses.length > 0 ? (
          <div className="overflow-x-auto">
            <table className="w-full text-left">
              <thead>
                <tr className="border-b border-white/10">
                  <th className="pb-3 text-white font-medium">Date</th>
                  <th className="pb-3 text-white font-medium">Description</th>
                  <th className="pb-3 text-white font-medium">Catégorie</th>
                  <th className="pb-3 text-white font-medium">Fournisseur</th>
                  <th className="pb-3 text-white font-medium">Montant</th>
                  <th className="pb-3 text-white font-medium">Méthode</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-white/10">
                {filteredExpenses.map((expense, index) => (
                  <tr key={expense.id} className="hover:bg-white/5 transition-colors">
                    <td className="py-3 text-white whitespace-nowrap">{formatDate(expense.date)}</td>
                    <td className="py-3 text-white">{expense.description}</td>
                    <td className="py-3">
                      <div className="flex items-center">
                        {getCategoryIcon(expense.category)}
                        <span className="ml-2 text-white">{getCategoryName(expense.category)}</span>
                      </div>
                    </td>
                    <td className="py-3 text-white">{expense.supplier}</td>
                    <td className="py-3 text-white font-medium">{formatCurrency(expense.amount)} DA</td>
                    <td className="py-3 text-white">{expense.paymentMethod}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <div className="text-center py-10 bg-white/5 rounded-lg">
            <BanknotesIcon className="h-12 w-12 text-white/20 mx-auto mb-4" />
            <p className="text-white font-medium">Aucune dépense trouvée</p>
            <p className="text-white/60 mt-1">Essayez de modifier vos filtres ou d'ajouter une nouvelle dépense</p>
          </div>
        )}
      </motion.div>

      {/* Modal d'ajout de dépense (maquette) */}
      {showAddExpenseModal && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50">
          <motion.div 
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            className="bg-white/10 backdrop-blur-lg rounded-xl p-6 border border-white/20 w-full max-w-lg"
          >
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-xl font-bold text-white">Ajouter une dépense</h2>
              <button
                onClick={() => setShowAddExpenseModal(false)}
                className="p-1 hover:bg-white/10 rounded-lg transition-colors"
              >
                <XMarkIcon className="h-6 w-6 text-white" />
              </button>
            </div>
            
            <p className="text-center text-white/60 py-10">
              Formulaire d'ajout de dépense en cours de développement.
              <br/><br/>
              Il permettra d'enregistrer de nouvelles dépenses avec détails complets et pièces justificatives.
            </p>
            
            <div className="flex justify-end mt-6">
              <button
                onClick={() => setShowAddExpenseModal(false)}
                className="px-4 py-2 bg-white/10 hover:bg-white/20 text-white rounded-lg transition-colors"
              >
                Fermer
              </button>
            </div>
          </motion.div>
        </div>
      )}
    </div>
  );
};

export default ExpenseTracker; 